import React, { useEffect, useState } from 'react';
import { useField, useFormikContext } from 'formik';
import { CSSProperties } from 'styled-components/macro';
import CountryCodePicker from 'lib/components/country-code-picker/CountryCodePicker';
import { FormikErrorMessage } from './FormikErrorMessage';
import { FormikLabel } from './FormikLabel';
import { Flex, FlexProps } from '../styles/layout';

import { UserValues } from 'app/pages/account/userAccount/editMyProfile/components/userValidator';
import { TextInput } from './FormikInputField';
import {
  combinePhoneFull,
  extractExtension,
  formatLocalNumber,
  parseLocalNumber,
  CountryCode,
  combinePhone,
  stripExtension,
} from 'app/pages/admin/users/contactHelper';

interface FormikPhoneFieldProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  name: string;
  label?: string;
  info?: string | React.ReactNode;
  isRequired: boolean;
  extendStyles?: {
    errorWrapper?: CSSProperties;
    labelStyles?: CSSProperties;
    inputWrapper?: FlexProps;
    mainWrapper?: FlexProps;
  };
  country: string;
  setCountry: (country: CountryCode) => void;
}

export const FormikPhoneInputField = React.forwardRef<
  HTMLInputElement,
  FormikPhoneFieldProps
>(
  (
    { label, isRequired, extendStyles, info, country, setCountry, ...props },
    ref
  ) => {
    const [field, meta] = useField(props);
    const { setFieldValue, values } = useFormikContext<UserValues>();

    const [local, setLocal] = useState<string>(
      parseLocalNumber(stripExtension(field.value || ''))
    );
    const [ext, setExt] = useState<string>(extractExtension(field.value || ''));

    useEffect(() => {
      const localParsed = parseLocalNumber(stripExtension(field.value || ''));
      const extParsed = extractExtension(field.value || '');
      setLocal(localParsed);
      setExt(extParsed);
    }, []);

    const handleCountryChange = (newCountry: CountryCode) => {
      setCountry(newCountry);
      const newFull = combinePhoneFull(newCountry, local, ext);
      setFieldValue(props.name, newFull);

      if (props.name === 'phone1') {
        const phone2Local = parseLocalNumber(values.phone2 || '');
        const phone2Ext = extractExtension(values.phone2 || '');
        const cellLocal = parseLocalNumber(values.cellPhone || '');
        setFieldValue(
          'phone2',
          combinePhoneFull(newCountry, phone2Local, phone2Ext)
        );
        setFieldValue('cellPhone', combinePhone(newCountry, cellLocal));
      }
    };

    const handleLocalChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newLocal = e.target.value;
      const valid = /^[0-9\s\-().]*$/.test(newLocal);
      if (!valid) return;

      const formattedLocal = formatLocalNumber(
        country as CountryCode,
        newLocal
      );
      setLocal(formattedLocal);
      setFieldValue(
        props.name,
        combinePhoneFull(country as CountryCode, formattedLocal, ext)
      );
    };

    const handleExtensionChange = (newExt: string) => {
      setExt(newExt);
      setFieldValue(
        props.name,
        combinePhoneFull(country as CountryCode, local, newExt)
      );
    };

    return (
      <Flex {...extendStyles?.mainWrapper} gap='4px'>
        {label && (
          <FormikLabel
            label={label}
            name={props.name}
            isRequired={isRequired}
            disabled={!!props.disabled}
            info={info}
            labelStyles={extendStyles?.labelStyles}
          />
        )}
        <Flex gap='12px' flexDirection='column'>
          <Flex {...extendStyles?.inputWrapper} gap='12px' flexDirection='row'>
            <CountryCodePicker
              ext={country}
              setExtension={handleCountryChange}
            />
            {props.name !== 'cellPhone' ? (
              <>
                <TextInput
                  {...field}
                  {...props}
                  id={props.name}
                  ref={ref}
                  value={local}
                  onChange={handleLocalChange}
                  style={{ flexBasis: '80%', flexGrow: 1 }}
                />
                <TextInput
                  placeholder='Ext (optional)'
                  value={ext}
                  onChange={e => handleExtensionChange(e.target.value)}
                  disabled={props.disabled}
                  style={{ flexBasis: '20%', maxWidth: '120px' }}
                />
              </>
            ) : (
              <TextInput
                {...field}
                {...props}
                id={props.name}
                ref={ref}
                value={local}
                onChange={handleLocalChange}
                style={{ width: '100%' }}
              />
            )}
          </Flex>
          <FormikErrorMessage meta={meta} extendStyles={extendStyles} />
        </Flex>
      </Flex>
    );
  }
);

FormikPhoneInputField.displayName = 'FormikPhoneInputField';
